import { NpsCampaignDto } from '@area-restrita/api-client/model/npsCampaignDto';
import { NpsCampaignDtoStatus } from '@area-restrita/api-client/model/npsCampaignDtoStatus';
import { NpsDistributionDto } from '@area-restrita/api-client/model/npsDistributionDto';

export const NPS_FIXED_QUESTION =
  'Em uma escala de 0 a 10, o quanto você recomendaria este serviço de transporte?';

export const NPS_SCALE_DESCRIPTION =
  'Escala fixa de 0 a 10, onde 0 significa nada provável e 10 extremamente provável.';

export const NPS_STATUS_LABELS: Record<NpsCampaignDtoStatus, string> = {
  draft: 'Rascunho',
  open: 'Aberta',
  closed: 'Encerrada',
};

export const NPS_OPEN_EXPORT_MIN_RESPONSES = 5;

export const formatDateTime = (value?: string | null) => {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDateRange = (campaign: NpsCampaignDto) =>
  `${formatDateTime(campaign.startAt)} - ${formatDateTime(campaign.endAt)}`;

export const formatTime = (value?: string | null) => {
  if (!value) return '-';

  const [hours, minutes] = value.split(':');

  if (!hours || !minutes) return value;

  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

type ScheduleDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

const SCHEDULE_DAY_LABELS: Record<ScheduleDay, string> = {
  mon: 'Seg',
  tue: 'Ter',
  wed: 'Qua',
  thu: 'Qui',
  fri: 'Sex',
  sat: 'Sáb',
  sun: 'Dom',
};

export const formatScheduleDays = (days?: readonly ScheduleDay[] | null) => {
  if (!days?.length) return '-';

  return days.map((day) => SCHEDULE_DAY_LABELS[day]).join(', ');
};

export const formatParticipationRate = (campaign: NpsCampaignDto) => {
  if (
    typeof campaign.responsesCount !== 'number' ||
    typeof campaign.eligibleUsersCount !== 'number' ||
    campaign.eligibleUsersCount === 0
  ) {
    return '-';
  }

  const rate = campaign.responsesCount / campaign.eligibleUsersCount;

  return formatPercent(rate);
};

export const formatPercent = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';

  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);
};

export const formatApiPercent = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';

  return formatPercent(value / 100);
};

export const formatMetricNumber = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';

  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 1,
  }).format(value);
};

export const formatInteger = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';

  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatApiNumber = (value: unknown) =>
  typeof value === 'number' ? formatMetricNumber(value) : '-';

export const formatSubmittedDate = (value?: string | null) => {
  if (!value) return '-';

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const shouldShowSegmentMetrics = ({
  insufficientData,
  nps,
  averageScore,
  distribution,
}: {
  insufficientData: boolean;
  nps: unknown;
  averageScore: unknown;
  distribution?: NpsDistributionDto | null;
}) =>
  !insufficientData &&
  typeof nps === 'number' &&
  typeof averageScore === 'number' &&
  Boolean(distribution);

export const isForbiddenError = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 403;

export const toDateTimeInputValue = (value?: string | null) => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

export const toIsoDateTime = (value: string) => new Date(value).toISOString();

export const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      message?: string;
      response?: { status?: number; data?: { message?: string } };
    };

    return maybeError.response?.data?.message ?? maybeError.message ?? fallback;
  }

  return fallback;
};

export const isConflictError = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 409;

export const parseFilenameFromContentDisposition = (
  contentDisposition?: string | null,
) => {
  if (!contentDisposition) return null;

  const encodedFilenameMatch = contentDisposition.match(
    /filename\*=([^']*)''([^;]+)/i,
  );

  if (encodedFilenameMatch?.[2]) {
    try {
      return decodeURIComponent(encodedFilenameMatch[2].trim());
    } catch {
      return encodedFilenameMatch[2].trim();
    }
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

  return filenameMatch?.[1]?.trim() || null;
};

const sanitizeFilenamePart = (value: string | number) => {
  const normalized = String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || 'sem-nome';
};

export const sanitizeDownloadFilename = (filename: string) => {
  const sanitized = filename
    .replace(/[/\\?%*:|"<>]/g, '-')
    // Control characters must be removed from browser download filenames.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]+/g, '')
    .replace(/^[.-]+/, '')
    .replace(/\s+/g, ' ')
    .trim();

  return sanitized.endsWith('.zip') ? sanitized : `${sanitized || 'nps'}.zip`;
};

export const getNpsExportFallbackFilename = (campaign: NpsCampaignDto) =>
  sanitizeDownloadFilename(
    [
      'nps',
      sanitizeFilenamePart(campaign.company.tradingName),
      sanitizeFilenamePart(campaign.name),
      sanitizeFilenamePart(NPS_STATUS_LABELS[campaign.status]),
    ].join('-'),
  );

export const getNpsExportFilename = (
  campaign: NpsCampaignDto,
  contentDisposition?: string | null,
) =>
  sanitizeDownloadFilename(
    parseFilenameFromContentDisposition(contentDisposition) ??
      getNpsExportFallbackFilename(campaign),
  );

export const getNpsExportEligibility = ({
  campaign,
  canExport,
  userType,
}: {
  campaign: NpsCampaignDto;
  canExport: boolean;
  userType: 'tenant' | 'company' | null;
}) => {
  const exportFlagAllowsUser =
    userType !== 'company' || campaign.exportAvailableToCompany;

  if (!canExport || campaign.status === 'draft' || !exportFlagAllowsUser) {
    return { shouldShow: false, enabled: false };
  }

  const responseCount = campaign.responsesCount;
  const hasVisibleResponseCount = typeof responseCount === 'number';

  if (
    hasVisibleResponseCount &&
    campaign.status === 'open' &&
    responseCount < NPS_OPEN_EXPORT_MIN_RESPONSES
  ) {
    return {
      shouldShow: true,
      enabled: false,
      disabledReason: `A exportação parcial exige pelo menos ${NPS_OPEN_EXPORT_MIN_RESPONSES} respostas. Esta campanha tem ${responseCount}.`,
    };
  }

  if (
    hasVisibleResponseCount &&
    userType === 'company' &&
    responseCount < NPS_OPEN_EXPORT_MIN_RESPONSES
  ) {
    return {
      shouldShow: true,
      enabled: false,
      disabledReason: `A exportação para usuários da empresa exige pelo menos ${NPS_OPEN_EXPORT_MIN_RESPONSES} respostas. Esta campanha tem ${responseCount}.`,
    };
  }

  return { shouldShow: true, enabled: true };
};

export const getNpsResultsEligibility = ({
  campaign,
  canViewDetails,
  userType,
}: {
  campaign: NpsCampaignDto;
  canViewDetails: boolean;
  userType: 'tenant' | 'company' | null;
}) => {
  if (campaign.status === 'draft') {
    return {
      enabled: false,
      disabledReason:
        'Os resultados estarão disponíveis após a abertura da campanha.',
    };
  }

  if (!canViewDetails) {
    return {
      enabled: false,
      disabledReason:
        'Os resultados desta campanha não estão disponíveis para o seu perfil.',
    };
  }

  if (userType === 'company' && !campaign.metricsVisibleToCompany) {
    return {
      enabled: false,
      disabledReason:
        'As métricas desta campanha não foram liberadas para a empresa.',
    };
  }

  return { enabled: true };
};

export const npsExportIncludesComments = ({
  campaign,
  canViewComments,
  userType,
}: {
  campaign: NpsCampaignDto;
  canViewComments: boolean;
  userType: 'tenant' | 'company' | null;
}) =>
  canViewComments &&
  (userType !== 'company' || campaign.commentsVisibleToCompany);
